import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Wrench, MapPin, Clock, CheckCircle, XCircle, Play, 
  Navigation, Camera, Phone, Building2, Zap, Star,
  ChevronRight, AlertCircle, RefreshCw, Send, Image,
  User, Calendar, DollarSign, FileText, Edit
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// İş Emri Durumları
const STATUS_CONFIG = {
  assigned: { label: 'Atandi', color: 'blue', icon: Clock, action: 'Kabul Et' },
  accepted: { label: 'Kabul Edildi', color: 'cyan', icon: CheckCircle, action: 'Yola Cik' },
  rejected: { label: 'Reddedildi', color: 'red', icon: XCircle },
  en_route: { label: 'Yolda', color: 'orange', icon: Navigation, action: 'Ulastim' },
  arrived: { label: 'Lokasyonda', color: 'yellow', icon: MapPin, action: 'Isi Tamamla' },
  in_progress: { label: 'Calisiyor', color: 'purple', icon: Wrench, action: 'Isi Tamamla' },
  completed: { label: 'Tamamlandi', color: 'green', icon: CheckCircle },
  pending_approval: { label: 'Onay Bekliyor', color: 'amber', icon: Clock },
  approved: { label: 'Onaylandi', color: 'emerald', icon: CheckCircle },
  payment_pending: { label: 'Odeme Bekliyor', color: 'indigo', icon: DollarSign },
  paid: { label: 'Odendi', color: 'green', icon: CheckCircle },
  cancelled: { label: 'Iptal', color: 'gray', icon: XCircle }
};

// İş Emri Kartı
const WorkOrderCard = ({ order, onStatusChange, onComplete, onOpenMap }) => {
  const statusInfo = STATUS_CONFIG[order.status] || STATUS_CONFIG.assigned;
  const StatusIcon = statusInfo.icon;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      default: return 'bg-green-500';
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Header */}
      <div className={`px-4 py-3 bg-${statusInfo.color}-50 dark:bg-${statusInfo.color}-900/20 border-b border-${statusInfo.color}-100 dark:border-${statusInfo.color}-800`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StatusIcon className={`w-5 h-5 text-${statusInfo.color}-600`} />
            <span className={`font-medium text-${statusInfo.color}-700 dark:text-${statusInfo.color}-400`}>
              {statusInfo.label}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${getPriorityColor(order.priority)}`} />
            {order.is_warranty && (
              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                Garanti
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {/* Başlık */}
        <div>
          <h3 className="font-semibold text-lg">{order.title}</h3>
          {order.description && (
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{order.description}</p>
          )}
        </div>

        {/* Lokasyon Bilgisi */}
        <div className="space-y-2">
          {/* Site */}
          <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-xl">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium">{order.site_name || 'Bilinmiyor'}</p>
              {order.site_address && (
                <p className="text-sm text-slate-500 line-clamp-1">{order.site_address}</p>
              )}
            </div>
            {order.site_latitude && order.site_longitude && (
              <button
                onClick={() => onOpenMap(order.site_latitude, order.site_longitude)}
                className="p-2 bg-primary text-white rounded-lg"
              >
                <Navigation className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* İstasyon */}
          <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-xl">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <Zap className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium">{order.station_name || 'Bilinmiyor'}</p>
            </div>
          </div>

          {/* Lokasyon Detayı */}
          {order.location_name && (
            <div className="flex items-start gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800">
              {order.location_photo_url ? (
                <img 
                  src={order.location_photo_url} 
                  alt={order.location_name}
                  className="w-16 h-16 rounded-lg object-cover"
                />
              ) : (
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                  <MapPin className="w-5 h-5 text-emerald-600" />
                </div>
              )}
              <div className="flex-1">
                <p className="font-medium text-emerald-800 dark:text-emerald-400">{order.location_name}</p>
                {order.location_floor && (
                  <p className="text-sm text-emerald-600">{order.location_floor}</p>
                )}
                {order.location_description && (
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    {order.location_description}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Atayan */}
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <User className="w-4 h-4" />
          <span>Atayan: {order.assigned_by_name || 'Sistem'}</span>
          <span className="mx-1">•</span>
          <Calendar className="w-4 h-4" />
          <span>{new Date(order.created_at).toLocaleDateString('tr-TR')}</span>
        </div>

        {/* Ücret Bilgisi */}
        {(order.labor_cost > 0 || order.travel_cost > 0) && (
          <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-700 dark:text-green-400">Kazanc</span>
            </div>
            <div className="text-right">
              <p className="font-bold text-green-700 dark:text-green-400">
                {(order.labor_cost + order.travel_cost).toFixed(0)} TL
              </p>
              <p className="text-xs text-green-600">
                {order.labor_cost} TL iscilik + {order.travel_cost} TL yol
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      {statusInfo.action && ['assigned', 'accepted', 'en_route', 'arrived', 'in_progress'].includes(order.status) && (
        <div className="px-4 pb-4">
          {order.status === 'assigned' ? (
            <div className="flex gap-2">
              <button
                onClick={() => onStatusChange(order.id, 'rejected')}
                className="flex-1 py-3 px-4 bg-red-100 text-red-700 font-medium rounded-xl hover:bg-red-200 transition-colors flex items-center justify-center gap-2"
              >
                <XCircle className="w-5 h-5" />
                Reddet
              </button>
              <button
                onClick={() => onStatusChange(order.id, 'accepted')}
                className="flex-1 py-3 px-4 bg-green-500 text-white font-medium rounded-xl hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Kabul Et
              </button>
            </div>
          ) : order.status === 'accepted' ? (
            <button
              onClick={() => onStatusChange(order.id, 'en_route')}
              className="w-full py-3 px-4 bg-orange-500 text-white font-medium rounded-xl hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
            >
              <Navigation className="w-5 h-5" />
              Yola Ciktim
            </button>
          ) : order.status === 'en_route' ? (
            <button
              onClick={() => onStatusChange(order.id, 'arrived')}
              className="w-full py-3 px-4 bg-yellow-500 text-white font-medium rounded-xl hover:bg-yellow-600 transition-colors flex items-center justify-center gap-2"
            >
              <MapPin className="w-5 h-5" />
              Lokasyona Ulastim
            </button>
          ) : (
            <button
              onClick={() => onComplete(order)}
              className="w-full py-3 px-4 bg-primary text-white font-medium rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              Isi Tamamla
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Tamamlama Modal
const CompletionModal = ({ isOpen, onClose, order, onSubmit }) => {
  const [notes, setNotes] = useState('');
  const [partsUsed, setPartsUsed] = useState('');
  const [partsCost, setPartsCost] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen || !order) return null;

  const handlePhotoAdd = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotos(prev => [...prev, ...files]);
  };

  const handleSubmit = async () => {
    if (!notes.trim()) {
      toast.error('Yapilan islem aciklamasi zorunludur');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(order.id, {
        completion_notes: notes,
        parts_used: partsUsed,
        parts_cost: partsCost,
        photos: photos
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-white dark:bg-slate-900 w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Isi Tamamla</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
          {/* Yapılan İşlem */}
          <div>
            <label className="block text-sm font-medium mb-2">Yapilan Islem *</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Yapilan islemi detayli aciklayiniz..."
              rows={4}
              className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 resize-none"
              required
            />
          </div>

          {/* Kullanılan Parçalar */}
          <div>
            <label className="block text-sm font-medium mb-2">Kullanilan Parcalar</label>
            <input
              type="text"
              value={partsUsed}
              onChange={(e) => setPartsUsed(e.target.value)}
              placeholder="orn: 2x Konnektor, 1x Kablo"
              className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800"
            />
          </div>

          {/* Parça Maliyeti */}
          <div>
            <label className="block text-sm font-medium mb-2">Parca Maliyeti (TL)</label>
            <input
              type="number"
              value={partsCost}
              onChange={(e) => setPartsCost(parseFloat(e.target.value) || 0)}
              placeholder="0"
              className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800"
            />
          </div>

          {/* Fotoğraflar */}
          <div>
            <label className="block text-sm font-medium mb-2">Fotograflar</label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handlePhotoAdd}
              accept="image/*"
              multiple
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full py-3 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl hover:border-primary transition-colors flex items-center justify-center gap-2"
            >
              <Camera className="w-5 h-5" />
              Fotograf Ekle
            </button>
            {photos.length > 0 && (
              <div className="flex gap-2 mt-2 overflow-x-auto pb-2">
                {photos.map((photo, idx) => (
                  <div key={idx} className="relative w-16 h-16 flex-shrink-0">
                    <img
                      src={URL.createObjectURL(photo)}
                      alt={`Foto ${idx + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <button
                      onClick={() => setPhotos(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Maliyet Özeti */}
          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Iscilik:</span>
              <span className="font-medium">{order.labor_cost} TL</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Yol:</span>
              <span className="font-medium">{order.travel_cost} TL</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Parca:</span>
              <span className="font-medium">{partsCost} TL</span>
            </div>
            <div className="pt-2 border-t dark:border-slate-700 flex justify-between">
              <span className="font-semibold">Toplam:</span>
              <span className="font-bold text-green-600">
                {(order.labor_cost + order.travel_cost + partsCost).toFixed(0)} TL
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t dark:border-slate-700">
          <button
            onClick={handleSubmit}
            disabled={submitting || !notes.trim()}
            className="w-full py-3 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Tamamla ve Gonder
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Ana Sayfa
export default function TechnicianPortalPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [completionModal, setCompletionModal] = useState({ open: false, order: null });
  const [gpsWatchId, setGpsWatchId] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    full_name: '',
    phone: '',
    email: '',
    city: '',
    district: '',
    address: '',
    specialties: [],
    service_regions: [],
    profile_description: '',
    iban: '',
    tax_id: ''
  });

  // GPS Takip
  const startGpsTracking = useCallback((orderId) => {
    if (!navigator.geolocation) {
      toast.error('GPS desteklenmiyor');
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        const token = localStorage.getItem('hims_token');
        try {
          await fetch(`${API_URL}/api/work-orders/${orderId}/gps`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy
            })
          });
        } catch (err) {
          console.error('GPS kayit hatasi:', err);
        }
      },
      (error) => {
        console.error('GPS hatasi:', error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5000
      }
    );

    setGpsWatchId(watchId);
  }, []);

  const stopGpsTracking = useCallback(() => {
    if (gpsWatchId) {
      navigator.geolocation.clearWatch(gpsWatchId);
      setGpsWatchId(null);
    }
  }, [gpsWatchId]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hims_token');
      
      const res = await fetch(`${API_URL}/api/technician-portal/my-orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setOrders(data);
      }
    } catch (error) {
      console.error('Siparişler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('hims_token');
      
      const res = await fetch(`${API_URL}/api/technician-portal/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('İstatistikler yüklenemedi:', error);
    }
  }, []);

  // Profil bilgilerini getir
  const fetchProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem('hims_token');
      
      const res = await fetch(`${API_URL}/api/technician-portal/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        setProfileForm({
          full_name: data.full_name || '',
          phone: data.phone || '',
          email: data.email || '',
          city: data.city || '',
          district: data.district || '',
          address: data.address || '',
          specialties: data.specialties || [],
          service_regions: data.service_regions || [],
          profile_description: data.profile_description || '',
          iban: data.iban || '',
          tax_id: data.tax_id || ''
        });
      }
    } catch (error) {
      console.error('Profil yüklenemedi:', error);
    }
  }, []);

  // Profil güncelle
  const handleProfileSave = async () => {
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('hims_token');
      
      const res = await fetch(`${API_URL}/api/technician-portal/profile`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileForm)
      });

      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        setEditingProfile(false);
        toast.success('Profil güncellendi');
        fetchStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Profil güncellenemedi');
      }
    } catch (error) {
      console.error('Profil guncelleme hatasi:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setSavingProfile(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchStats();
    fetchProfile();

    // 30 saniyede bir güncelle
    const interval = setInterval(() => {
      fetchOrders();
      fetchStats();
    }, 30000);

    return () => {
      clearInterval(interval);
      stopGpsTracking();
    };
  }, [fetchOrders, fetchStats, fetchProfile, stopGpsTracking]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('hims_token');
      
      // GPS konumunu al
      let latitude, longitude;
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          latitude = pos.coords.latitude;
          longitude = pos.coords.longitude;
        } catch (err) {
          console.log('GPS alinamadi:', err);
        }
      }

      const res = await fetch(`${API_URL}/api/work-orders/${orderId}/status?status=${newStatus}${latitude ? `&latitude=${latitude}&longitude=${longitude}` : ''}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success(`Durum guncellendi: ${STATUS_CONFIG[newStatus]?.label}`);
        
        // Yola çıkıldıysa GPS takibini başlat
        if (newStatus === 'en_route') {
          startGpsTracking(orderId);
        }
        // Varıldıysa veya tamamlandıysa GPS takibini durdur
        if (['arrived', 'completed'].includes(newStatus)) {
          stopGpsTracking();
        }
        
        fetchOrders();
        fetchStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Durum guncellenemedi');
      }
    } catch (error) {
      toast.error('Bir hata olustu');
    }
  };

  const handleComplete = async (orderId, data) => {
    try {
      const token = localStorage.getItem('hims_token');
      
      const formData = new FormData();
      formData.append('completion_notes', data.completion_notes);
      if (data.parts_used) formData.append('parts_used', data.parts_used);
      formData.append('parts_cost', data.parts_cost || 0);
      
      data.photos?.forEach(photo => {
        formData.append('photos', photo);
      });

      const res = await fetch(`${API_URL}/api/work-orders/${orderId}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success('Is emri tamamlandi, onay bekleniyor');
        stopGpsTracking();
        fetchOrders();
        fetchStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Is emri tamamlanamadi');
      }
    } catch (error) {
      toast.error('Bir hata olustu');
      throw error;
    }
  };

  const openMap = (lat, lng) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
  };

  const activeOrders = orders.filter(o => ['assigned', 'accepted', 'en_route', 'arrived', 'in_progress'].includes(o.status));
  const completedOrders = orders.filter(o => ['completed', 'pending_approval', 'approved', 'payment_pending', 'paid'].includes(o.status));

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-4 pb-16">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Wrench className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-lg font-semibold">{stats?.full_name || user?.full_name || 'Usta Portal'}</h1>
              <p className="text-sm text-white/80">Hosgeldiniz</p>
            </div>
          </div>
          <button onClick={fetchOrders} className="p-2 bg-white/20 rounded-lg">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/20 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Aktif Is</span>
            </div>
            <p className="text-2xl font-bold">{stats?.active_orders || 0}</p>
          </div>
          <div className="bg-white/20 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Tamamlanan</span>
            </div>
            <p className="text-2xl font-bold">{stats?.completed_orders || 0}</p>
          </div>
          <div className="bg-white/20 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Star className="w-4 h-4" />
              <span className="text-sm">Puan</span>
            </div>
            <p className="text-2xl font-bold">{(stats?.average_rating || 0).toFixed(1)}</p>
          </div>
          <div className="bg-white/20 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm">Bu Ay</span>
            </div>
            <p className="text-2xl font-bold">{(stats?.monthly_earnings || 0).toFixed(0)} TL</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 -mt-8">
        {/* Tabs - 4 sekme */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-1 flex mb-4 overflow-x-auto">
          <button
            onClick={() => setActiveTab('active')}
            className={`flex-1 py-2 px-3 rounded-lg font-medium transition-colors whitespace-nowrap text-sm ${
              activeTab === 'active' 
                ? 'bg-primary text-white' 
                : 'text-slate-600 dark:text-slate-400'
            }`}
          >
            Aktif ({activeOrders.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex-1 py-2 px-3 rounded-lg font-medium transition-colors whitespace-nowrap text-sm ${
              activeTab === 'completed' 
                ? 'bg-primary text-white' 
                : 'text-slate-600 dark:text-slate-400'
            }`}
          >
            Tamamlanan
          </button>
          <button
            onClick={() => setActiveTab('earnings')}
            className={`flex-1 py-2 px-3 rounded-lg font-medium transition-colors whitespace-nowrap text-sm ${
              activeTab === 'earnings' 
                ? 'bg-primary text-white' 
                : 'text-slate-600 dark:text-slate-400'
            }`}
          >
            Kazançlar
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 py-2 px-3 rounded-lg font-medium transition-colors whitespace-nowrap text-sm ${
              activeTab === 'profile' 
                ? 'bg-primary text-white' 
                : 'text-slate-600 dark:text-slate-400'
            }`}
          >
            Profilim
          </button>
        </div>

        {/* Aktif İşler */}
        {activeTab === 'active' && (
          loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : activeOrders.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-2xl shadow">
              <Wrench className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium mb-2">Aktif İş Emri Yok</h3>
              <p className="text-slate-500">Yeni iş emirleri burada görünecek</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activeOrders.map(order => (
                <WorkOrderCard
                  key={order.id}
                  order={order}
                  onStatusChange={handleStatusChange}
                  onComplete={(o) => setCompletionModal({ open: true, order: o })}
                  onOpenMap={openMap}
                />
              ))}
            </div>
          )
        )}

        {/* Tamamlanan İşler */}
        {activeTab === 'completed' && (
          loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : completedOrders.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-2xl shadow">
              <CheckCircle className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium mb-2">Tamamlanan İş Yok</h3>
              <p className="text-slate-500">Tamamladığınız işler burada listelenecek</p>
            </div>
          ) : (
            <div className="space-y-4">
              {completedOrders.map(order => (
                <WorkOrderCard
                  key={order.id}
                  order={order}
                  onStatusChange={handleStatusChange}
                  onComplete={(o) => setCompletionModal({ open: true, order: o })}
                  onOpenMap={openMap}
                />
              ))}
            </div>
          )
        )}

        {/* Kazançlar Sekmesi */}
        {activeTab === 'earnings' && (
          <div className="space-y-4">
            {/* Özet Kartları */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow">
                <div className="flex items-center gap-2 mb-2 text-green-600">
                  <DollarSign className="w-5 h-5" />
                  <span className="text-sm font-medium">Bu Ay</span>
                </div>
                <p className="text-2xl font-bold">{(stats?.monthly_earnings || 0).toFixed(0)} ₺</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow">
                <div className="flex items-center gap-2 mb-2 text-blue-600">
                  <DollarSign className="w-5 h-5" />
                  <span className="text-sm font-medium">Toplam</span>
                </div>
                <p className="text-2xl font-bold">{(stats?.total_earnings || 0).toFixed(0)} ₺</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow">
                <div className="flex items-center gap-2 mb-2 text-orange-600">
                  <Clock className="w-5 h-5" />
                  <span className="text-sm font-medium">Bekleyen</span>
                </div>
                <p className="text-2xl font-bold">{(stats?.pending_payment || 0).toFixed(0)} ₺</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow">
                <div className="flex items-center gap-2 mb-2 text-purple-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">Ödenen</span>
                </div>
                <p className="text-2xl font-bold">{(stats?.paid_amount || 0).toFixed(0)} ₺</p>
              </div>
            </div>

            {/* Son Ödemeler */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Son Ödemeler
              </h3>
              {completedOrders.filter(o => o.status === 'paid').length === 0 ? (
                <p className="text-center text-slate-500 py-8">Henüz ödeme kaydı yok</p>
              ) : (
                <div className="space-y-3">
                  {completedOrders.filter(o => o.status === 'paid').slice(0, 5).map(order => (
                    <div key={order.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                      <div>
                        <p className="font-medium">{order.title}</p>
                        <p className="text-sm text-slate-500">{order.site_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">{(order.labor_cost + order.travel_cost).toFixed(0)} ₺</p>
                        <p className="text-xs text-slate-500">
                          {order.paid_at ? new Date(order.paid_at).toLocaleDateString('tr-TR') : '-'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Profil Sekmesi */}
        {activeTab === 'profile' && (
          <div className="space-y-4">
            {/* Onay Durumu Banner */}
            {profile && !profile.is_verified && (
              <div className={`p-4 rounded-xl ${profile.profile_completed ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800' : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'}`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`w-5 h-5 mt-0.5 ${profile.profile_completed ? 'text-orange-600' : 'text-yellow-600'}`} />
                  <div>
                    <h4 className={`font-medium ${profile.profile_completed ? 'text-orange-800 dark:text-orange-300' : 'text-yellow-800 dark:text-yellow-300'}`}>
                      {profile.profile_completed ? 'Onay Bekleniyor' : 'Profilinizi Tamamlayın'}
                    </h4>
                    <p className={`text-sm ${profile.profile_completed ? 'text-orange-600 dark:text-orange-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                      {profile.profile_completed 
                        ? 'Profiliniz admin onayı bekliyor. Onaylandıktan sonra iş almaya başlayabilirsiniz.'
                        : 'İş alabilmek için profilinizi eksiksiz doldurun ve onay için gönderin.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Profil Düzenleme Formu */}
            {editingProfile ? (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow p-6 space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                  <Edit className="w-5 h-5 text-primary" />
                  Profil Bilgilerini Düzenle
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Ad Soyad</label>
                    <input
                      type="text"
                      value={profileForm.full_name}
                      onChange={(e) => setProfileForm({...profileForm, full_name: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Telefon</label>
                    <input
                      type="text"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">E-posta</label>
                    <input
                      type="email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">TC / Vergi No</label>
                    <input
                      type="text"
                      value={profileForm.tax_id}
                      onChange={(e) => setProfileForm({...profileForm, tax_id: e.target.value})}
                      placeholder="TC Kimlik veya Vergi No"
                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">İl</label>
                    <select
                      value={profileForm.city}
                      onChange={(e) => setProfileForm({...profileForm, city: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                    >
                      <option value="">Seçiniz</option>
                      {['İstanbul', 'Ankara', 'İzmir', 'Bursa', 'Antalya', 'Adana', 'Konya', 'Gaziantep', 'Kocaeli', 'Mersin', 'Diyarbakır', 'Kayseri', 'Eskişehir', 'Samsun', 'Denizli'].map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">İlçe</label>
                    <input
                      type="text"
                      value={profileForm.district}
                      onChange={(e) => setProfileForm({...profileForm, district: e.target.value})}
                      placeholder="İlçe adı"
                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Açık Adres</label>
                  <textarea
                    value={profileForm.address}
                    onChange={(e) => setProfileForm({...profileForm, address: e.target.value})}
                    placeholder="Mahalle, sokak, bina no..."
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Uzmanlık Alanları</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: 'ac_charger', label: 'AC Şarj' },
                      { key: 'dc_charger', label: 'DC Hızlı Şarj' },
                      { key: 'software', label: 'Yazılım/OCPP' },
                      { key: 'electrical', label: 'Elektrik' },
                      { key: 'general', label: 'Genel Bakım' }
                    ].map(spec => (
                      <button
                        key={spec.key}
                        type="button"
                        onClick={() => {
                          const specs = profileForm.specialties.includes(spec.key)
                            ? profileForm.specialties.filter(s => s !== spec.key)
                            : [...profileForm.specialties, spec.key];
                          setProfileForm({...profileForm, specialties: specs});
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                          profileForm.specialties.includes(spec.key)
                            ? 'bg-primary text-white border-primary'
                            : 'border-slate-300 dark:border-slate-600 hover:border-primary'
                        }`}
                      >
                        {spec.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Hizmet Bölgeleri (virgülle ayırın)</label>
                  <input
                    type="text"
                    value={profileForm.service_regions.join(', ')}
                    onChange={(e) => setProfileForm({...profileForm, service_regions: e.target.value.split(',').map(r => r.trim()).filter(r => r)})}
                    placeholder="Kadıköy, Üsküdar, Beşiktaş..."
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Kendinizi Tanıtın</label>
                  <textarea
                    value={profileForm.profile_description}
                    onChange={(e) => setProfileForm({...profileForm, profile_description: e.target.value})}
                    placeholder="Deneyimleriniz, yetenekleriniz hakkında kısa bir açıklama..."
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">IBAN</label>
                  <input
                    type="text"
                    value={profileForm.iban}
                    onChange={(e) => setProfileForm({...profileForm, iban: e.target.value})}
                    placeholder="TR..."
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleProfileSave}
                    disabled={savingProfile}
                    className="flex-1 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {savingProfile ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        Kaydet
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setEditingProfile(false)}
                    className="px-6 py-3 border border-slate-200 dark:border-slate-700 rounded-xl font-medium hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    İptal
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Profil Kartı - Okuma Modu */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-10 h-10 text-primary" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold">{profile?.full_name || stats?.full_name || user?.full_name}</h2>
                        <p className="text-slate-500">{profile?.technician_type === 'hims_team' ? 'HIMS Ekibi' : profile?.technician_type === 'subcontractor' ? 'Taşeron' : 'Servis Firması'}</p>
                        {profile?.is_verified ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full mt-1">
                            <CheckCircle className="w-3 h-3" />
                            Doğrulanmış Usta
                          </span>
                        ) : profile?.profile_completed ? (
                          <span className="inline-flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-full mt-1">
                            <Clock className="w-3 h-3" />
                            Onay Bekliyor
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full mt-1">
                            <AlertCircle className="w-3 h-3" />
                            Profil Eksik
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setEditingProfile(true)}
                      className="p-3 bg-primary/10 text-primary rounded-xl hover:bg-primary/20"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                  </div>

                  {/* İstatistikler */}
                  <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-slate-50 dark:bg-slate-900 rounded-xl">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-primary">{stats?.completed_orders || 0}</p>
                      <p className="text-xs text-slate-500">İş Tamamlandı</p>
                    </div>
                    <div className="text-center border-x dark:border-slate-700">
                      <p className="text-2xl font-bold text-yellow-500">{(stats?.average_rating || 0).toFixed(1)}</p>
                      <p className="text-xs text-slate-500">Puan</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-500">{(stats?.total_earnings || 0).toFixed(0)}₺</p>
                      <p className="text-xs text-slate-500">Toplam Kazanç</p>
                    </div>
                  </div>

                  {/* Bilgiler */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                      <Phone className="w-5 h-5 text-slate-400" />
                      <span>{profile?.phone || '-'}</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                      <MapPin className="w-5 h-5 text-slate-400" />
                      <span>{profile?.city ? `${profile.city}${profile.district ? ', ' + profile.district : ''}` : 'Konum belirtilmemiş'}</span>
                    </div>
                    {profile?.address && (
                      <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                        <Building2 className="w-5 h-5 text-slate-400 mt-0.5" />
                        <span>{profile.address}</span>
                      </div>
                    )}
                    {profile?.service_regions?.length > 0 && (
                      <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                        <p className="text-sm text-slate-500 mb-2">Hizmet Bölgeleri:</p>
                        <div className="flex flex-wrap gap-2">
                          {profile.service_regions.map((region, idx) => (
                            <span key={idx} className="px-2 py-1 bg-primary/10 text-primary text-sm rounded-full">
                              {region}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {profile?.profile_description && (
                      <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                        <p className="text-sm text-slate-500 mb-1">Hakkımda:</p>
                        <p className="text-slate-700 dark:text-slate-300">{profile.profile_description}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Uzmanlık Alanları */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-primary" />
                    Uzmanlık Alanları
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {(profile?.specialties || ['general']).map((spec, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg text-sm">
                        {spec === 'ac_charger' ? 'AC Şarj' : 
                         spec === 'dc_charger' ? 'DC Hızlı Şarj' : 
                         spec === 'software' ? 'Yazılım/OCPP' :
                         spec === 'electrical' ? 'Elektrik' : 'Genel Bakım'}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Üyelik Durumu */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl shadow p-4 text-white">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Star className="w-5 h-5" />
                      Üyelik Durumu
                    </h3>
                    <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                      {profile?.membership_type === 'premium' ? 'Premium' : profile?.membership_type === 'basic' ? 'Basic' : 'Ücretsiz'}
                    </span>
                  </div>
                  <p className="text-sm text-white/80">
                    {profile?.membership_type === 'free' 
                      ? 'Premium üyelik ile daha fazla iş fırsatı ve öncelikli eşleştirme avantajlarından yararlanın!'
                      : `Üyeliğiniz ${profile?.membership_expires ? new Date(profile.membership_expires).toLocaleDateString('tr-TR') : 'süresiz'} tarihine kadar geçerli.`}
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Completion Modal */}
      <CompletionModal
        isOpen={completionModal.open}
        onClose={() => setCompletionModal({ open: false, order: null })}
        order={completionModal.order}
        onSubmit={handleComplete}
      />
    </div>
  );
}
